/* eslint-disable react/prop-types */
import themeGet from "@styled-system/theme-get";
import { get, isUndefined, isArray } from "lodash";
import { Icon, Flex, Loading } from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Treebeard, decorators } from "react-treebeard";
import styled from "styled-components";

import GroupTypeBadge from "src/components/Badge/GroupTypeBadge";

import StaticTreeViewStyle from "./TreeView.json";

const NewWindowIcon = styled(Icon)`
  bottom: 2px;
  position: relative;
  z-index: 2;
  opacity: 0.5;
  transition: opacity 300ms ease-in-out;
  &:hover {
    opacity: 1;
  }
`;

const TreeGroupExpandIconFlexWrapper = styled.button`
  border: none;
  background: none;
  appearance: none;
  z-index: 2;
  cursor: pointer;
  padding: ${themeGet("space.xs")};
  margin-right: ${themeGet("space.s")};
`;

const TreeHeaderTitle = styled.div`
  border: none;
  background: none;
  appearance: none;
  display: inline-block;
  z-index: 2;
  display: flex;
  align-items: center;
  font-size: 1.4rem;
  text-align: left;
  margin-right: ${themeGet("space.s")};
  text-decoration: ${({ isHidden }) => (isHidden ? "line-through" : "none")};
  font-weight: ${({ isActive }) => (isActive ? "bold" : 300)};
  margin-left: ${({ hasChildren }) => (hasChildren ? "0px" : "11px")};
  line-height: 1.8rem;
  padding: 0 ${themeGet("space.s")};
`;
const TreeHeaderIcon = styled(Icon)`
  margin-right: 5px;
`;
export const TreeHeader = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;

  &:before {
    content: "";
    position: absolute;
    background: transparent;
    height: 100%;
    width: calc(100% + 1000px);
    top: 0;
    left: -1000px;
    transition: background 200ms ease-in-out;
  }

  &:hover {
    &:before {
      background: rgba(156, 224, 248, 0.3);
    }
  }

  &:hover ${TreeHeaderIcon} {
    color: black;
  }
`;
const TreeStatus = styled.div`
  display: inline-block;
  margin-left: 6px;
  display: flex;
  align-items: center;
  > * + * {
    margin-left: 5px;
  }
`;
class TreeContainer extends decorators.Container {
  renderToggleDecorator() {
    const { style, node } = this.props;
    return <Toggle style={style.toggle} node={node} />;
  }
}

const Toggle = ({ node }) => (
  <TreeGroupExpandIconFlexWrapper
    aria-expanded={node.toggled ? "true" : "false"}
    aria-label={node.toggled ? "Collapse tree node" : "Expand tree node"}
    flexDirection="column"
    alignItems="center"
  >
    <Icon icon={["fal", "chevron-right"]} size="1x" color="black" />
  </TreeGroupExpandIconFlexWrapper>
);

const LocalLink = ({ url, target }) => (
  <Link title="Open details" to={url} target={target}>
    <NewWindowIcon
      ml="xs"
      color="black"
      icon={["fas", "external-link-alt"]}
      size="xs"
    />
  </Link>
);

const buildLink = (generateLink, node, linkOpensInNewTab) => {
  if (!node) {
    return null;
  }

  const url = generateLink(node);
  if (!url) {
    return null;
  }

  return (
    <LocalLink url={url} target={linkOpensInNewTab ? "_blank" : "_self"} />
  );
};

const buildHeader =
  ({
    nodeStatus,
    onActivate,
    onToggle,
    refreshTree,
    cursor,
    withGroupTypeBadge,
    noTreeHeaderIcon,
    customizeTreeHeader,
    flipOnClickHeader,
    generateLink,
    linkOpensInNewTab,
  }) =>
  (treeNode) => {
    const { node } = treeNode;
    const hasChildren = Boolean(node.children);

    useEffect(() => {
      if (node.active) {
        cursor.current = node;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (customizeTreeHeader) {
      const result = customizeTreeHeader(treeNode, {
        onActivate,
        refreshTree,
      });
      if (!isUndefined(result)) {
        return result;
      }
    }

    const NodeStatus = nodeStatus || (() => null);
    const activeNodeProps = node.active
      ? {
          id: "active-node",
          "data-testid": "active-node-test",
        }
      : {};

    const onClickHeader = (e) => {
      e.stopPropagation();
      if (flipOnClickHeader && hasChildren) {
        onToggle(node, !node.toggled);
      } else {
        onActivate(node);
      }
    };

    return (
      <TreeHeader hasChildren={hasChildren} onClick={onClickHeader}>
        <TreeHeaderTitle
          data-testid={node.active ? "treeheader-node-bold" : "treeheader-node"}
          isActive={node.active}
          isHidden={get(node, "group.isHidden")}
          hasChildren={hasChildren}
        >
          <span data-testid="treeheader-node-name" {...activeNodeProps}>
            {node.name}
          </span>
          {generateLink && buildLink(generateLink, node, linkOpensInNewTab)}

          <TreeStatus>
            <NodeStatus node={node} />
          </TreeStatus>
        </TreeHeaderTitle>
        <Flex alignItems="center">
          {withGroupTypeBadge && node && <GroupTypeBadge group={node} ml="s" />}
          {!hasChildren && !noTreeHeaderIcon && (
            <TreeHeaderIcon
              color={node.active ? "black" : "grey"}
              icon={
                node.active ? ["fas", "angle-right"] : ["far", "angle-right"]
              }
              size="1x"
              ml="s"
            />
          )}
        </Flex>
      </TreeHeader>
    );
  };

const updateTreeObject = (tree) => {
  if (isArray(tree)) {
    return [...tree];
  }

  return { ...tree };
};

const TreeView = ({
  data,
  "data-testid": testId,
  className,
  onNodeSelected,
  nodeStatus,
  withGroupTypeBadge = false,
  noTreeHeaderIcon = false,
  customizeTreeHeader,
  flipOnClickHeader = false,
  treeViewStyle = StaticTreeViewStyle,
  generateLink = () => null,
  linkOpensInNewTab = true,
}) => {
  const [treeData, setTreeData] = useState(data);
  const cursor = useRef();

  useEffect(() => {
    setTreeData(data);
  }, [data]);

  useEffect(() => {
    TreeView.scrollActiveNodeIntoView();
  }, []);

  const refreshTree = () => {
    setTreeData((td) => updateTreeObject(td));
  };

  // onToggle is used for expanding nodes
  const onToggle = (node, toggled) => {
    // If node doesn't want to be mutated
    if (node.noActivation) {
      return;
    }

    // most of this code has been pinched from the https://github.com/storybookjs/react-treebeard README.md
    // I've added comments on what I think it's doing
    if (cursor.current) {
      // reset the old node to inactive
      cursor.current.active = false;
    }

    // set the new node to active
    node.active = true;
    if (node.children) {
      // toggle it if it has children
      node.toggled = toggled;
    }

    // track the cursor/active node
    cursor.current = node;

    // refresh the render
    refreshTree();

    // notify our callbacks something new has been selected
    if (onNodeSelected) {
      onNodeSelected(node, refreshTree);
    }
  };

  // onActivate is used on leaf node clicks
  const onActivate = (node) => {
    if (cursor.current) {
      // reset the old node to inactive
      cursor.current.active = false;
    }

    if (node.noActivation) {
      return;
    }

    // set the new node to active
    node.active = true;

    // track the cursor/active node
    cursor.current = node;

    // refresh the render
    refreshTree();

    if (onNodeSelected) {
      onNodeSelected(node, refreshTree);
    }
  };

  const Header = buildHeader({
    nodeStatus,
    onActivate,
    onToggle,
    refreshTree,
    cursor,
    withGroupTypeBadge,
    noTreeHeaderIcon,
    customizeTreeHeader,
    flipOnClickHeader,
    generateLink,
    linkOpensInNewTab,
  });

  return (
    <div data-testid={testId}>
      <Treebeard
        animations={false}
        className={className}
        data={treeData}
        style={treeViewStyle}
        onToggle={onToggle}
        decorators={{
          ...decorators,
          Header,
          Toggle,
          Loading,
          Container: TreeContainer,
        }}
      />
    </div>
  );
};

TreeView.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  "data-testid": PropTypes.string,
  className: PropTypes.string,
  onNodeSelected: PropTypes.func,
  nodeStatus: PropTypes.func,
  customizeTreeHeader: PropTypes.func,
  flipOnClickHeader: PropTypes.bool,
};

TreeView.scrollActiveNodeIntoView = (
  block = "end",
  selector = "#active-node"
) => {
  const elem = document.querySelector(selector);
  if (elem) {
    elem.scrollIntoView({ behavior: "smooth", block });
  }
};

export default TreeView;
